import React, { useState } from 'react';
import { 
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer,
  LineChart, Line, XAxis, Tooltip, YAxis, CartesianGrid,
  BarChart, Bar
} from 'recharts';
import TaskForm from '../TaskForm';
import { useDialog } from '../DialogContext'; 

const VINTAGE_BEIGE = "#e5d1a5"; 
const JERUSALEM_GOLD = "#c5a059"; 

const DashboardCore = ({ 
  player, setPlayer, handleNewTask, completeQuest, 
  inputs, setInputs, addHabit, toggleHabit, ICONS,
  updateQuest, deleteHabit, updateHabit 
}) => {

  const [editingQuest, setEditingQuest] = useState(null);
  const [editQuestText, setEditQuestText] = useState("");
  const [editingHabit, setEditingHabit] = useState(null);
  const [editHabitText, setEditHabitText] = useState("");
  const [isLeisureDropdownOpen, setIsLeisureDropdownOpen] = useState(false);

  const { showConfirm, showAlert } = useDialog(); 

  const startEditingQuest = (q) => { setEditingQuest(q.id); setEditQuestText(q.text); };
  const submitQuestEdit = (id) => { updateQuest(id, editQuestText); setEditingQuest(null); };

  const startEditingHabit = (h) => { setEditingHabit(h.id); setEditHabitText(h.text); };
  const submitHabitEdit = (id) => { updateHabit(id, editHabitText); setEditingHabit(null); };

  const dayMap = { 'Pzt': 'Mon', 'Sal': 'Tue', 'Çar': 'Wed', 'Per': 'Thu', 'Cum': 'Fri', 'Cmt': 'Sat', 'Paz': 'Sun' };
  const translatedGraph = player.historyGraph?.map(data => ({
    ...data, day: dayMap[data.day] || data.day 
  })) || [];

  const panelStyle = {
    backgroundColor: 'var(--sidebar-bg)',
    border: `1px solid var(--faded-line, ${JERUSALEM_GOLD})`,
    boxShadow: 'var(--panel-shadow, 0 15px 35px rgba(0,0,0,0.5))',
    borderRadius: '4px',
    padding: '25px',
    display: 'flex',
    flexDirection: 'column',
    height: '100%' 
  };

  const titleStyle = {
    fontFamily: 'Cinzel, serif',
    color: 'var(--accent-gold, ' + JERUSALEM_GOLD + ')',
    borderBottom: `1px dashed var(--muted-gold, ${JERUSALEM_GOLD})`,
    paddingBottom: '10px',
    marginBottom: '20px',
    fontSize: '1.3rem',
    letterSpacing: '2px',
    textTransform: 'uppercase',
    textShadow: '1px 1px 3px rgba(0,0,0,0.8)'
  };

  const getQuestStyle = (val) => {
    if (val >= 50) return { color: '#ff4d4d', border: 'var(--accent-red)', glow: 'rgba(139, 58, 58, 0.4)', label: 'EPIC' };
    if (val >= 30) return { color: JERUSALEM_GOLD, border: JERUSALEM_GOLD, glow: 'rgba(197, 160, 89, 0.2)', label: 'HARD' };
    if (val >= 20) return { color: VINTAGE_BEIGE, border: VINTAGE_BEIGE, glow: 'transparent', label: 'MEDIUM' };
    return { color: 'rgba(229, 209, 165, 0.5)', border: 'rgba(229, 209, 165, 0.3)', glow: 'transparent', label: 'EASY' };
  };

  const leisureOptions = [
    { value: 300000, label: "5 Minutes" },
    { value: 900000, label: "15 Minutes" },
    { value: 1200000, label: "20 Minutes" },
    { value: 1800000, label: "30 Minutes" },
    { value: 3600000, label: "1 Hour" }
  ];
  const currentLeisure = leisureOptions.find(o => o.value === player.conscienceSettings?.leisureBudget) || leisureOptions[2];

  const todayStr = new Date().toDateString();

  const habitChartData = [];
  const shortDayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  let totalRitualsLast7Days = 0;

  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const dateString = d.toDateString();
    const shortDay = shortDayNames[d.getDay()];
    
    const count = player.habits?.filter(h => h.completedDates?.includes(dateString)).length || 0;
    totalRitualsLast7Days += count;
    habitChartData.push({ day: shortDay, count: count });
  }

  const maxRituals = Math.max(4, ...habitChartData.map(d => d.count));

  const handleHabitToggle = (e, id) => {
    e.stopPropagation(); 
    toggleHabit(id);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
      
      <style>{`
        input::placeholder, 
        textarea::placeholder, 
        .vintage-input::placeholder,
        .quest-input::placeholder {
          color: var(--muted-gold, #c5a059) !important;
          opacity: 0.9 !important;
          font-style: italic;
        }
        input, select, textarea {
          color: var(--ink-color, #fdfbf7) !important;
        }
      `}</style>

      {/* --- ÜST SIRA: ANALİTİK --- */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '25px' }}>
        
        <div style={panelStyle}>
          <h3 style={titleStyle}>Character Build</h3>
          <ResponsiveContainer width="100%" height={260}>
            <RadarChart cx="50%" cy="50%" outerRadius="75%" data={Object.keys(player.stats).map(k=>({s:k.toUpperCase(), v:player.stats[k]}))}>
              <PolarGrid stroke="rgba(197, 160, 89, 0.3)" />
              <PolarAngleAxis dataKey="s" tick={{fill: 'var(--sidebar-text, ' + VINTAGE_BEIGE + ')', fontSize:11, fontFamily:'Courier Prime', fontWeight: 'bold'}} />
              <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} axisLine={false} />
              <Radar name="Stats" dataKey="v" stroke="var(--accent-red)" strokeWidth={2} fill="var(--accent-red)" fillOpacity={0.4} />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        <div style={panelStyle}>
          <h3 style={titleStyle}>Growth Velocity</h3>
          <ResponsiveContainer width="100%" height={260}>
            {translatedGraph.length > 0 ? (
              <LineChart data={translatedGraph}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(197, 160, 89, 0.15)" vertical={false} />
                <XAxis dataKey="day" stroke="var(--muted-gold)" tick={{fontSize: 12, fontFamily:'Courier Prime', opacity: 0.8}} axisLine={false} tickLine={false} dy={10} />
                <YAxis stroke="var(--muted-gold)" tick={{fontSize: 12, fontFamily:'Courier Prime', opacity: 0.8}} axisLine={false} tickLine={false} dx={-10} />
                <Tooltip contentStyle={{backgroundColor: 'rgba(0,0,0,0.8)', border: `1px solid ${JERUSALEM_GOLD}`, fontFamily:'Courier Prime', borderRadius: '4px', color: VINTAGE_BEIGE}} itemStyle={{color: JERUSALEM_GOLD, fontWeight: 'bold'}} />
                <Line type="monotone" dataKey="xp" stroke="var(--accent-gold, #c5a059)" strokeWidth={3} dot={{r: 4, fill: 'var(--accent-red)', strokeWidth: 0}} activeDot={{r: 6, fill: JERUSALEM_GOLD, stroke: 'var(--sidebar-bg)', strokeWidth: 2}} />
              </LineChart>
            ) : (
              <div style={{display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', opacity: 0.5, fontFamily: 'Courier Prime, monospace', color: VINTAGE_BEIGE}}>
                Gathering system data...
              </div>
            )}
          </ResponsiveContainer>
        </div>
      </div>

      {/* --- ORTA SIRA: AKSİYON --- */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '25px', alignItems: 'stretch' }}>
        
        {/* THE QUEST BOARD */}
        <div style={panelStyle}>
          <h3 style={titleStyle}>The Quest Board</h3>
          
          <div style={{marginBottom: '20px', paddingBottom: '20px', borderBottom: `1px dashed var(--faded-line, rgba(197, 160, 89, 0.3))`}}>
            <TaskForm onTaskAdded={handleNewTask} /> 
          </div>

          <ul style={{listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '15px'}}>
            {player.activeQuests?.map(q => {
              const styleData = getQuestStyle(q.val);
              
              return (
                <li key={q.id} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  backgroundColor: 'rgba(0,0,0,0.25)', padding: '15px 20px', borderRadius: '4px',
                  border: `1px solid var(--faded-line, rgba(197, 160, 89, 0.2))`, borderLeft: `4px solid ${styleData.border}`,
                  boxShadow: `0 4px 10px rgba(0,0,0,0.2), inset 20px 0 30px -20px ${styleData.glow}`,
                  transition: 'transform 0.2s'
                }}>
                  <div style={{flex: 1, display: 'flex', flexDirection: 'column', gap: '8px'}}>
                    
                    <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                      <span style={{fontSize: '0.7rem', fontFamily: 'Cinzel, serif', fontWeight: 'bold', letterSpacing: '1px', color: styleData.color, border: `1px solid ${styleData.border}`, padding: '2px 6px', borderRadius: '2px'}}>
                        {styleData.label}
                      </span>
                      <span style={{color: 'var(--accent-gold, #c5a059)', fontFamily: 'Courier Prime, monospace', fontSize: '0.85rem'}}>
                        +{q.val} XP
                      </span>
                      <span style={{color: 'var(--muted-gold)', opacity: 0.7, fontSize: '0.85rem'}}>
                        {ICONS[q.stat] || ' '}
                      </span>
                    </div>

                    {editingQuest === q.id ? (
                       <input 
                         className="vintage-input" style={{width: '95%', backgroundColor: 'rgba(255,255,255,0.05)', border: `1px dashed ${JERUSALEM_GOLD}`, padding: '5px', outline: 'none', fontFamily: 'Courier Prime, monospace'}}
                         value={editQuestText} onChange={(e) => setEditQuestText(e.target.value)}
                         onBlur={() => submitQuestEdit(q.id)}
                         onKeyDown={(e) => e.key === 'Enter' && submitQuestEdit(q.id)}
                         autoFocus
                       />
                    ) : (
                       <span onClick={() => startEditingQuest(q)} style={{cursor: 'text', lineHeight: '1.5', fontFamily: 'Courier Prime, monospace', fontSize: '1.05rem', color: 'var(--ink-color, #fdfbf7)'}} title="Click to edit">
                         {q.text}
                       </span>
                    )}
                  </div>
                  
                  <div style={{display: 'flex', gap: '8px'}}>
                    <button 
                      style={{background: 'transparent', border: '1px solid var(--muted-gold)', color: 'var(--muted-gold)', width: '35px', height: '35px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', borderRadius: '4px', transition: '0.2s'}}
                      onMouseOver={(e) => { e.currentTarget.style.borderColor = 'var(--accent-red)'; e.currentTarget.style.color = 'var(--accent-red)'; }}
                      onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--muted-gold)'; e.currentTarget.style.color = 'var(--muted-gold)'; }}
                      onClick={() => showConfirm("Abandon this quest?", () => setPlayer(prev => ({...prev, activeQuests: prev.activeQuests.filter(x=>x.id !== q.id)})), "Abandon Quest")} 
                      title="Abandon Quest"
                    >✕</button>
                    <button 
                      style={{background: 'var(--accent-red)', border: '1px solid #5a0d0e', color: '#fff', width: '35px', height: '35px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', borderRadius: '4px', transition: '0.2s', fontWeight: 'bold'}}
                      onMouseOver={(e) => { e.currentTarget.style.backgroundColor = 'var(--accent-red-hover, #8a1417)'; e.currentTarget.style.borderColor = 'var(--accent-gold)'; }}
                      onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'var(--accent-red)'; e.currentTarget.style.borderColor = '#5a0d0e'; }}
                      onClick={()=>completeQuest(q.id)} 
                      title="Complete Quest"
                    >✓</button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* DAILY RITUALS */}
        <div style={panelStyle}>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px dashed var(--muted-gold, ${JERUSALEM_GOLD})`, paddingBottom: '10px', marginBottom: '20px'}}>
            <h3 style={{...titleStyle, borderBottom: 'none', paddingBottom: 0, marginBottom: 0}}>Daily Rituals</h3>
          </div>

          <div style={{height: '100px', marginBottom: '15px', backgroundColor: 'rgba(0,0,0,0.15)', borderRadius: '4px', padding: '10px', border: '1px solid var(--faded-line)'}}>
            {totalRitualsLast7Days === 0 ? (
              <div style={{display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', opacity: 0.6, fontFamily: 'Courier Prime, monospace', fontSize: '0.85rem', color: 'var(--muted-gold)', fontStyle: 'italic'}}>
                No rituals completed in the last 7 days...
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={habitChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{fill: 'var(--muted-gold)', fontSize: 10, opacity: 0.8, fontFamily: 'Courier Prime'}} dy={5} />
                  <YAxis hide domain={[0, maxRituals]} />
                  <Tooltip 
                    cursor={{fill: 'rgba(255, 255, 255, 0.05)'}} 
                    contentStyle={{backgroundColor: 'rgba(0,0,0,0.9)', border: `1px solid var(--accent-gold)`, fontFamily:'Courier Prime', fontSize: '0.8rem', color: 'var(--sidebar-text)'}}
                    itemStyle={{color: 'var(--accent-red)'}}
                  />
                  <Bar dataKey="count" name="Rituals" fill="var(--accent-red)" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
          
          <div style={{display: 'flex', gap: '10px', marginBottom: '25px', paddingBottom: '20px', borderBottom: `1px dashed var(--faded-line, rgba(197, 160, 89, 0.3))`}}>
            <input 
              className="vintage-input" 
              style={{flex: 1, backgroundColor: 'transparent', border: 'none', borderBottom: `1px solid var(--muted-gold)`, outline: 'none', fontFamily: 'Courier Prime, monospace', padding: '10px'}} 
              value={inputs.habitName} 
              onChange={e=>setInputs({...inputs, habitName:e.target.value})} 
              placeholder="Forging a new ritual..." 
              onKeyDown={(e) => e.key === 'Enter' && addHabit()}
            />
            <button 
              className="wax-seal-btn" 
              style={{width: '45px', height: '45px', fontSize: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--accent-red)', color: '#fff', border: 'none', cursor: 'pointer', borderRadius: '4px'}} 
              onClick={addHabit}
            >
              +
            </button>
          </div>

          <ul style={{listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px'}}>
            {player.habits?.map(h => {
              const completedDates = h.completedDates || (h.completed ? [todayStr] : []);
              const isDoneToday = completedDates.includes(todayStr);

              const streakBlocks = [];
              const dayInitials = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
              
              for (let i = 6; i >= 0; i--) {
                const d = new Date();
                d.setDate(d.getDate() - i);
                const isDone = completedDates.includes(d.toDateString());
                const initial = dayInitials[d.getDay()];
                
                streakBlocks.push(
                  <div 
                    key={i} 
                    title={d.toDateString()}
                    style={{
                      width: '16px', height: '16px', 
                      backgroundColor: isDone ? 'var(--accent-red)' : 'rgba(0,0,0,0.4)',
                      border: `1px solid ${isDone ? '#5a0d0e' : 'var(--faded-line)'}`,
                      borderRadius: '2px',
                      display: 'flex', justifyContent: 'center', alignItems: 'center',
                      color: isDone ? '#fff' : 'var(--muted-gold)',
                      fontSize: '9px', fontWeight: 'bold', fontFamily: 'Courier Prime, monospace',
                      transition: '0.3s', opacity: isDone ? 1 : 0.6
                    }} 
                  >
                    {initial}
                  </div>
                );
              }

              return (
              <li key={h.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px', 
                backgroundColor: 'rgba(0,0,0,0.25)', border: `1px solid var(--faded-line)`, borderRadius: '4px',
                opacity: isDoneToday ? 0.7 : 1, transition: 'all 0.3s'
              }}>
                <div style={{flex: 1, display: 'flex', alignItems: 'flex-start', gap: '15px'}}>
                  
                  <div 
                    onClick={(e) => handleHabitToggle(e, h.id)}
                    style={{
                      width: '24px', height: '24px', border: `1px solid var(--accent-gold)`, borderRadius: '4px',
                      backgroundColor: isDoneToday ? 'var(--accent-red)' : 'rgba(0,0,0,0.4)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s',
                      boxShadow: isDoneToday ? `0 0 10px rgba(139, 58, 58, 0.6)` : 'none',
                      cursor: 'pointer', flexShrink: 0, marginTop: '2px'
                    }}>
                    {isDoneToday && <span style={{color: 'var(--accent-gold)', fontSize: '1rem', fontWeight: 'bold', pointerEvents: 'none'}}>✓</span>}
                  </div>
                  
                  <div style={{display: 'flex', flexDirection: 'column', flex: 1}}>
                    {editingHabit === h.id ? (
                      <input 
                        className="vintage-input" style={{backgroundColor: 'transparent', borderBottom: `1px dashed var(--accent-gold)`, outline: 'none', fontFamily: 'Courier Prime, monospace', width: '100%'}}
                        value={editHabitText} onChange={(e) => setEditHabitText(e.target.value)}
                        onBlur={() => submitHabitEdit(h.id)}
                        onKeyDown={(e) => e.key === 'Enter' && submitHabitEdit(h.id)}
                        autoFocus
                      />
                    ) : (
                      <span 
                        onClick={() => startEditingHabit(h)} 
                        title="Click to edit" 
                        style={{fontFamily:'Courier Prime, monospace', fontSize: '1.05rem', color: isDoneToday ? 'var(--muted-gold)' : 'var(--ink-color)', textDecoration: isDoneToday ? 'line-through' : 'none', transition: '0.3s', cursor: 'text'}}
                      >
                        {h.text}
                      </span>
                    )}
                    
                    <div style={{display: 'flex', gap: '5px', marginTop: '10px'}}>
                      {streakBlocks}
                    </div>
                  </div>
                </div>
                
                <button 
                  onClick={() => showConfirm("Remove this ritual from your daily cycle?", () => deleteHabit(h.id), "Delete Ritual")} 
                  style={{background: 'none', border: 'none', color: 'var(--muted-gold)', cursor: 'pointer', opacity: 0.5, fontSize: '1.2rem', padding: '0 5px', transition: '0.2s', marginLeft: '10px'}} 
                  onMouseOver={(e) => { e.currentTarget.style.color = 'var(--accent-red)'; e.currentTarget.style.opacity = 1; }}
                  onMouseOut={(e) => { e.currentTarget.style.color = 'var(--muted-gold)'; e.currentTarget.style.opacity = 0.5; }}
                  title="Delete Ritual"
                >
                  ✕
                </button>
              </li>
            )})}
          </ul>
        </div>

      </div>

      {/* --- ALT SIRA: DIGITAL CONSCIENCE SHIELD --- */}
      {/* Container için z-index eklendi (Dropdown altında kalmasın diye) */}
      <div style={{...panelStyle, height: 'auto', borderTop: '4px solid var(--accent-red)', position: 'relative', zIndex: 10}}>
        
        <div style={{position: 'absolute', top: '-50px', left: '-50px', width: '150px', height: '150px', background: 'radial-gradient(circle, rgba(139,58,58,0.15) 0%, transparent 70%)', borderRadius: '50%', pointerEvents: 'none'}}></div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '30px', borderBottom: `1px dashed var(--muted-gold)`, paddingBottom: '15px', position: 'relative', zIndex: 1 }}>
          {/* Mavi emoji kaldırıldı, yerine rengi altın olabilen Shogi taşı/Kalkan sembolü kondu */}
          <span style={{ fontSize: '2.5rem', color: 'var(--accent-gold)', textShadow: '0 0 15px rgba(197, 160, 89, 0.4)' }}>☗</span>
          <div>
            <h3 style={{fontFamily: 'Cinzel, serif', color: 'var(--accent-gold)', margin: 0, fontSize: '1.6rem', letterSpacing: '2px', textTransform: 'uppercase'}}>Digital Conscience Shield</h3>
            <p style={{fontSize: '0.95rem', opacity: 0.85, margin: '5px 0 0 0', fontFamily: 'Courier Prime, monospace', color: 'var(--sidebar-text)'}}>
              Configure your Edge AI parameters. This syncs directly with your browser extension.
            </p>
          </div>
        </div>

        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '25px', position: 'relative'}}>
          
          <div style={{textAlign: 'left'}}>
            <label style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', fontSize: '0.9rem', color: 'var(--accent-gold)', fontWeight: 'bold', fontFamily: 'Cinzel, serif', marginBottom: '8px', letterSpacing: '1px'}}>
              <span>Vision Tags</span>
              <span style={{color: '#16d14e', opacity: 0.9, fontSize: '0.7rem', letterSpacing: '0', fontFamily: 'Courier Prime, monospace', padding: '2px 6px', backgroundColor: 'rgba(212, 175, 55, 0.1)', borderRadius: '2px', border: '1px solid rgba(212, 175, 55, 0.2)'}}>
                [ INFINITE TIME ]
              </span>
            </label>
            <input 
              className="vintage-input" 
              value={player.conscienceSettings?.visionTags || ""} 
              onChange={e => setPlayer(prev => ({...prev, conscienceSettings: {...prev.conscienceSettings, visionTags: e.target.value}}))} 
              style={{width: '100%', backgroundColor: 'rgba(0,0,0,0.2)', border: `1px solid var(--faded-line)`, padding: '12px 14px', outline: 'none', borderRadius: '4px', fontFamily: 'Courier Prime, monospace', fontSize: '1rem', color: '#fff', boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.3)', transition: 'border-color 0.2s'}} 
              placeholder="e.g. ai, data science, coding..." 
              onFocus={(e) => e.currentTarget.style.borderColor = 'var(--accent-gold)'}
              onBlur={(e) => e.currentTarget.style.borderColor = 'var(--faded-line)'}
            />
          </div>

          <div style={{textAlign: 'left'}}>
            <label style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', fontSize: '0.9rem', color: 'var(--accent-gold)', fontWeight: 'bold', fontFamily: 'Cinzel, serif', marginBottom: '8px', letterSpacing: '1px'}}>
              <span>Kryptonite Tags</span>
              <span style={{color: '#ff6b6b', opacity: 1, fontSize: '0.7rem', letterSpacing: '0', fontFamily: 'Courier Prime, monospace', padding: '2px 6px', backgroundColor: 'rgba(255, 107, 107, 0.1)', borderRadius: '2px', border: '1px solid rgba(255, 107, 107, 0.2)'}}>
                [ 15S MAX ]
              </span>
            </label>
            <input 
              className="vintage-input" 
              value={player.conscienceSettings?.kryptoniteTags || ""} 
              onChange={e => setPlayer(prev => ({...prev, conscienceSettings: {...prev.conscienceSettings, kryptoniteTags: e.target.value}}))} 
              style={{width: '100%', backgroundColor: 'rgba(0,0,0,0.2)', border: `1px solid var(--faded-line)`, padding: '12px 14px', outline: 'none', borderRadius: '4px', fontFamily: 'Courier Prime, monospace', fontSize: '1rem', color: '#fff', boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.3)', transition: 'border-color 0.2s'}} 
              placeholder="e.g. gossip, drama, fashion..." 
              onFocus={(e) => e.currentTarget.style.borderColor = '#ff6b6b'}
              onBlur={(e) => e.currentTarget.style.borderColor = 'var(--faded-line)'}
            />
          </div>

          <div style={{textAlign: 'left', position: 'relative'}}>
            <label style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', fontSize: '0.9rem', color: 'var(--accent-gold)', fontWeight: 'bold', fontFamily: 'Cinzel, serif', marginBottom: '8px', letterSpacing: '1px'}}>
              <span>Leisure Budget</span>
              <span style={{color: '#a0aab5', opacity: 0.9, fontSize: '0.7rem', letterSpacing: '0', fontFamily: 'Courier Prime, monospace', padding: '2px 6px', backgroundColor: 'rgba(160, 170, 181, 0.1)', borderRadius: '2px', border: '1px solid rgba(160, 170, 181, 0.2)'}}>
                [ GRAY AREA ]
              </span>
            </label>
            
            <div>
              <div 
                className="vintage-input"
                style={{width: '100%', backgroundColor: 'rgba(0,0,0,0.2)', border: `1px solid var(--faded-line)`, padding: '12px 14px', outline: 'none', borderRadius: '4px', fontFamily: 'Courier Prime, monospace', fontSize: '1rem', color: '#fff', boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.3)', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}
                onClick={() => setIsLeisureDropdownOpen(!isLeisureDropdownOpen)}
              >
                <span>{currentLeisure.label}</span>
                <span style={{fontSize: '0.8rem', color: 'var(--accent-gold)'}}>▼</span>
              </div>
              
              {/* Dropdown Z-index düzeltildi ve pozisyon absolute olarak ayarlandı */}
              {isLeisureDropdownOpen && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 5px)', left: 0, right: 0, zIndex: 100,
                  backgroundColor: 'var(--paper-bg)', border: `1px solid var(--accent-gold)`,
                  boxShadow: '0 15px 30px rgba(0,0,0,0.9)', borderRadius: '4px', overflow: 'hidden'
                }}>
                  {leisureOptions.map((opt) => (
                    <div 
                      key={opt.value}
                      style={{padding: '10px 15px', cursor: 'pointer', fontFamily: 'Courier Prime, monospace', color: 'var(--sidebar-text)', borderBottom: '1px solid rgba(255,255,255,0.05)', transition: 'background-color 0.2s', fontSize: '0.95rem'}}
                      onMouseOver={(e) => { e.currentTarget.style.backgroundColor = 'var(--accent-red)'; e.currentTarget.style.color = '#fff'; }}
                      onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--sidebar-text)'; }}
                      onClick={() => {
                        setPlayer(prev => ({...prev, conscienceSettings: {...prev.conscienceSettings, leisureBudget: opt.value}}));
                        setIsLeisureDropdownOpen(false);
                      }}
                    >
                      {opt.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>

        {/* Sync Butonu inceltildi ve z-index geriye atıldı (Dropdown üzerine çıkmasın) */}
        <button 
          className="wax-seal-btn" 
          style={{
            position: 'relative', zIndex: 1,
            width: '100%', borderRadius: '4px', padding: '12px', height: 'auto', marginTop: '30px', 
            fontSize: '1.1rem', letterSpacing: '2px', fontFamily: 'Cinzel, serif', fontWeight: 'bold',
            backgroundColor: 'var(--accent-red)', color: '#fff', border: '1px solid #3d0708',
            transition: 'all 0.2s', boxShadow: '0 4px 10px rgba(0,0,0,0.3)'
          }} 
          onMouseOver={(e) => { e.currentTarget.style.backgroundColor = 'var(--accent-red-hover, #9e1b1b)'; e.currentTarget.style.borderColor = 'var(--accent-gold)'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 15px rgba(0,0,0,0.4)'; }}
          onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'var(--accent-red)'; e.currentTarget.style.borderColor = '#3d0708'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 10px rgba(0,0,0,0.3)'; }}
          onClick={() => { 
            // Kurşun geçirmez data paketi: Tanımsızsa boş string veya default değer yolla
            const syncPayload = { 
              type: "LAG_SYNC_ALL", 
              visionTags: player.conscienceSettings?.visionTags || "", 
              kryptoniteTags: player.conscienceSettings?.kryptoniteTags || "", 
              leisureBudget: player.conscienceSettings?.leisureBudget || currentLeisure.value, 
              quests: player.activeQuests || []
            };
            
            window.postMessage(syncPayload, "*"); 
            
            showAlert("Edge AI parameters have been successfully calibrated and synchronized with your extension.", "Shield Synchronized"); 
          }}
        >
          SYNC EDGE AI
        </button>
      </div>
      
    </div>
  );
};

export default DashboardCore;